import { Component, DestroyRef, inject } from "@angular/core";
import { AsyncPipe, DecimalPipe } from "@angular/common";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { CacheService } from "./cache.service";
import { LoadingService } from "../loading/loading.service";
import { ShortCache } from "../../shared/contract/Cache/ShortCache";
import { OptionService } from "../option/option.service";
import { NotificationService } from "@services/notification.service";
import { catchError, finalize, map, switchMap, tap } from "rxjs";
import { TimeService } from "@services/time.service";
import { SpatialRequest } from "src/app/shared/contract/SpatialAnalysis/SpatialRequest";
import { ModuleEnum } from "src/app/shared/contract/Enum/ModuleEnum";
import { CACHE_OVERLAY_Z_INDEX } from "../core/constants/z-index";
import { MapLayerService } from "src/app/shared/services/map-layer.service";
import { DialogContainerComponent } from "@components/dialog-container/dialog-container.component";
import { MatIcon } from "@angular/material/icon";
import { TableSkeletonLoaderComponent } from "../skeleton-presets/table-skeleton-loader/table-skeleton-loader.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";
import { ThematicInfoService } from "@services/real-estate/thematic-info/thematic-info.service";
import { MapCompareableLayerService } from "@services/map-compareable-layer.service";
import { PortfolioItemDataService } from "../portfolio-item/services/portfolio-item-data.service";

@Component({
  selector: "app-cache",
  templateUrl: "./cache.component.html",
  styleUrls: ["./cache.component.scss"],
  imports: [DialogContainerComponent, MatIcon, TableSkeletonLoaderComponent, TranslocoPipe, AsyncPipe]
})
export class CacheComponent {
  readonly cacheService = inject(CacheService);
  readonly timeService = inject(TimeService);
  readonly optionService = inject(OptionService);
  private readonly matDialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly loadingService = inject(LoadingService);
  private readonly translocoService = inject(TranslocoService);
  private readonly decimalPipe = inject(DecimalPipe);
  private readonly notificationService = inject(NotificationService);
  private readonly mapLayerService = inject(MapLayerService);
  private readonly thematicInfoService = inject(ThematicInfoService);
  private readonly mapCompareableLayerService = inject(MapCompareableLayerService);
  private readonly portfolioItemDataService = inject(PortfolioItemDataService);

  isLoading = true;
  readonly Z_INDEX_CACHE_OVERLAY = CACHE_OVERLAY_Z_INDEX;
  readonly Modules = ModuleEnum;
  readonly loadCacheOverview$ = this.cacheService.loadCacheOverview().pipe(
    map(caches => caches.map(cache => ({ ...cache, details: this.getDetails(cache) }))),
    finalize(() => (this.isLoading = false))
  );

  protected openCacheObject(log: ShortCache) {
    this.loadingService.show();

    this.cacheService
      .loadCacheRequestData(log.id)
      .pipe(
        tap(() => {
          this.mapLayerService.removeAllMapLayerWithoutMap();
          this.thematicInfoService.resetThematicInformation();
          this.mapCompareableLayerService.removeAllLayers();
          this.portfolioItemDataService.reset();
        }),
        switchMap(cacheRequestData => {
          switch (log.moduleId) {
            case ModuleEnum.APS:
            case ModuleEnum.CommercialAVM:
            case ModuleEnum.BPS:
            case ModuleEnum.Rvm:
            case ModuleEnum.MixedRealEstate:
              return this.cacheService.openObjectFromCache(log.id, log.moduleId);
            case ModuleEnum.SpatialAnalysis: {
              return this.cacheService.navigateToSpatialAnalysisWithCache(log.id, cacheRequestData as SpatialRequest);
            }
            default:
              throw new Error("No Mapping Enum Found");
          }
        }),
        finalize(() => {
          this.matDialog.closeAll();
          this.loadingService.hide();
        }),
        takeUntilDestroyed(this.destroyRef),
        catchError(error => {
          console.error(error);
          this.notificationService.defaultError();
          throw error;
        })
      )
      .subscribe();
  }

  protected closeDialog() {
    this.matDialog.closeAll();
  }

  private getDetails(log: ShortCache) {
    switch (log.moduleId) {
      case ModuleEnum.APS:
      case ModuleEnum.CommercialAVM:
      case ModuleEnum.Rvm:
      case ModuleEnum.MixedRealEstate:
        return log.address.zip + " " + log.address.city + " " + log.address.street + " " + log.address.houseNumber;
      case ModuleEnum.BPS:
        if (log.address?.zip && log.address?.city && log.address?.street && log.address?.houseNumber) {
          return log.address.zip + " " + log.address.city + " " + log.address.street + " " + log.address.houseNumber;
        }

        return (
          this.translocoService.translate("Module.Address.Coordinates.Latitude") +
          ": " +
          this.decimalPipe.transform(log.address.coordinate.latitude, "1.4-4", this.optionService.locale()) +
          " " +
          this.translocoService.translate("Module.Address.Coordinates.Longitude") +
          ": " +
          this.decimalPipe.transform(log.address.coordinate.longitude, "1.4-4", this.optionService.locale())
        );
      case ModuleEnum.SpatialAnalysis:
        return this.translocoService.translate("Module.Address.AddressCount") + ": " + log.countAddress;
    }
  }
}
